import type { DungeonFloorConfig } from "@/dungeon/dungeon-floor-config";
import { allowanceTotal, normalizeMonsterAllowance } from "@/dungeon/dungeon-shop-types";
import type { HomeEconomy } from "@/home/economy";
import type { TowerFloorSpawnRow } from "@/tower/tower-floor-config";
import { spawnCountForMonster } from "@/dungeon/dungeon-floor-purchases";

export type DungeonMonsterVault = Record<string, number>;
export type DungeonMonsterGoldBought = Record<string, number>;

export function normalizeDungeonMonsterVault(raw: unknown): DungeonMonsterVault {
  if (!raw || typeof raw !== "object") return {};
  const out: DungeonMonsterVault = {};
  for (const [id, val] of Object.entries(raw)) {
    if (typeof val !== "number" || !Number.isFinite(val)) continue;
    const n = Math.max(0, Math.floor(val));
    if (n > 0) out[id] = n;
  }
  return out;
}

export function normalizeDungeonMonsterGoldBought(raw: unknown): DungeonMonsterGoldBought {
  if (!raw || typeof raw !== "object") return {};
  const out: DungeonMonsterGoldBought = {};
  for (const [id, val] of Object.entries(raw)) {
    if (typeof val !== "number" || !Number.isFinite(val)) continue;
    const n = Math.max(0, Math.floor(val));
    if (n > 0) out[id] = n;
  }
  return out;
}

export function vaultStock(economy: HomeEconomy, monsterId: string): number {
  return economy.dungeonMonsterVault[monsterId] ?? 0;
}

export function goldBoughtCount(economy: HomeEconomy, monsterId: string): number {
  return economy.dungeonMonsterGoldBought[monsterId] ?? 0;
}

export function sumSpawnCountsByMonster(spawns: TowerFloorSpawnRow[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of spawns) {
    if (!row.monsterId) continue;
    out[row.monsterId] = (out[row.monsterId] ?? 0) + Math.max(0, Math.floor(row.count));
  }
  return out;
}

export function deployedOnOtherFloors(
  floorConfigs: DungeonFloorConfig[],
  excludeFloor: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const cfg of floorConfigs) {
    if (cfg.floor === excludeFloor) continue;
    const byMonster = sumSpawnCountsByMonster(cfg.spawns ?? []);
    for (const [id, n] of Object.entries(byMonster)) {
      out[id] = (out[id] ?? 0) + n;
    }
  }
  return out;
}

/** Max total units of monsterId assignable on this floor (all rows combined). */
export function maxMonsterTotalOnFloor(
  economy: HomeEconomy,
  savedSpawnsOnFloor: TowerFloorSpawnRow[],
  monsterId: string,
): number {
  const savedOnFloor = spawnCountForMonster(savedSpawnsOnFloor, monsterId);
  return vaultStock(economy, monsterId) + savedOnFloor;
}

export function migrateEconomyMonsterVaultFromLegacyFloors(
  economy: HomeEconomy,
  floorConfigs: DungeonFloorConfig[],
): { economy: HomeEconomy; changed: boolean } {
  if (economy.dungeonMonsterVaultMigrated) {
    return { economy, changed: false };
  }
  let vault = { ...economy.dungeonMonsterVault };
  let goldBought = { ...economy.dungeonMonsterGoldBought };
  for (const cfg of floorConfigs) {
    const allowance = normalizeMonsterAllowance(cfg.monsterAllowance);
    const ids = new Set<string>([
      ...Object.keys(allowance),
      ...(cfg.spawns ?? []).map((s) => s.monsterId),
    ]);
    for (const id of ids) {
      const cap = allowanceTotal(allowance, id);
      const used = spawnCountForMonster(cfg.spawns ?? [], id);
      const free = Math.max(0, cap - used);
      if (free > 0) {
        vault[id] = (vault[id] ?? 0) + free;
      }
      const slot = allowance[id];
      if (slot && slot.gold > 0) {
        goldBought[id] = (goldBought[id] ?? 0) + slot.gold;
      }
    }
  }

  return {
    economy: {
      ...economy,
      dungeonMonsterVault: vault,
      dungeonMonsterGoldBought: goldBought,
      dungeonMonsterVaultMigrated: true,
    },
    changed: true,
  };
}

export function commitFloorSpawnsToVault(
  economy: HomeEconomy,
  savedSpawns: TowerFloorSpawnRow[],
  nextSpawns: TowerFloorSpawnRow[],
): { ok: true; economy: HomeEconomy } | { ok: false; reason: string } {
  const savedBy = sumSpawnCountsByMonster(savedSpawns);
  const nextBy = sumSpawnCountsByMonster(nextSpawns);
  const ids = new Set([...Object.keys(savedBy), ...Object.keys(nextBy)]);
  let vault = { ...economy.dungeonMonsterVault };

  for (const id of ids) {
    const oldN = savedBy[id] ?? 0;
    const newN = nextBy[id] ?? 0;
    const delta = newN - oldN;
    if (delta === 0) continue;
    const stock = vault[id] ?? 0;
    if (delta > 0 && stock < delta) {
      return { ok: false, reason: `คลังมอนสเตอร์ไม่พอ (${id})` };
    }
    const nextStock = stock - delta;
    if (nextStock <= 0) {
      delete vault[id];
    } else {
      vault[id] = nextStock;
    }
  }

  return { ok: true, economy: { ...economy, dungeonMonsterVault: vault } };
}

export function allowanceCapForReadiness(
  economy: HomeEconomy,
  savedSpawnsOnFloor: TowerFloorSpawnRow[],
  monsterId: string,
): number {
  return maxMonsterTotalOnFloor(economy, savedSpawnsOnFloor, monsterId);
}
